// Breaks the login loop that follows when the API rejects every token.
//
// A 401 from the API ends the session and lands on /login, which redirects to
// Zitadel automatically. While the Zitadel session is still alive that
// redirect returns at once with a fresh token, and if the API rejects that one
// too (e.g. its signing-key cache is stale after a Zitadel upgrade), the app
// reloads, fails and redirects again, forever. Each server rejection is
// recorded here, per tab; after a few in quick succession /login stops
// redirecting and asks the user instead.

const KEY = "sessionRejections";
const WINDOW_MS = 60_000;
const MAX_REJECTIONS = 2;

function recent(now: number): number[] {
  try {
    const raw = sessionStorage.getItem(KEY);
    const list: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(list)
      ? list.filter(
          (t): t is number => typeof t === "number" && now - t < WINDOW_MS,
        )
      : [];
  } catch {
    return [];
  }
}

/** Records that the API rejected the current session's token. */
export function recordSessionRejection(now = Date.now()): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify([...recent(now), now]));
  } catch {
    // Storage unavailable: the guard simply never trips.
  }
}

/** True while rejections repeat too fast for automatic sign-in to help. */
export function isSessionRejectionLoop(now = Date.now()): boolean {
  return recent(now).length >= MAX_REJECTIONS;
}

/** Forgets past rejections, once the API accepts a token again or the user retries. */
export function clearSessionRejections(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
}
